'use client';

/**
 * PipelineView Component
 *
 * Visualizes a pipeline with stages in a horizontal flow.
 * Shows stage cards with name, color, count, and SLA status.
 * Connects stages with TransitionArrow sub-component.
 */

import Box from '@mui/material/Box';
import { styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useMemo, useState } from 'react';
import type { NeonAccent } from '../../theme/palette';
import { getNeonStyles, neonAccents } from '../../theme/palette';
import { glassEffects, neonGlow } from '../../theme/shadows';
import { motionPresets, transitionPresets } from '../../theme/transitions';
import type { Pipeline, PipelineStage, SLAStatus } from '../../types/pipeline';
import { transientOptions } from '../../utils/styledUtils';
import { TransitionArrow } from './TransitionArrow';

// ============================================================================
// Types
// ============================================================================

export interface PipelineViewProps {
  /** Pipeline definition */
  pipeline: Pipeline;
  /** Record counts per stage (stageId -> count) */
  stageCounts?: Record<string, number>;
  /** Currently selected stage ID */
  selectedStageId?: string;
  /** Stage click handler */
  onStageClick?: (stage: PipelineStage) => void;
  /** Show SLA indicators */
  showSLA?: boolean;
  /** SLA status per stage (stageId -> status) */
  slaStatus?: Record<string, SLAStatus>;
  /** Neon accent color */
  neonAccent?: NeonAccent;
  /** Display variant */
  variant?: 'compact' | 'detailed';
  /** Additional class name */
  className?: string;
}

// ============================================================================
// Styled Components
// ============================================================================

interface StyledContainerProps {
  $isDark: boolean;
}

const StyledContainer = styled(
  Box,
  transientOptions,
)<StyledContainerProps>(({ $isDark }) => {
  const glass = $isDark ? glassEffects.dark : glassEffects.light;

  return {
    display: 'flex',
    alignItems: 'center',
    padding: '16px',
    overflowX: 'auto',
    overflowY: 'hidden',
    borderRadius: 16,
    background: glass.background,
    border: `1px solid ${glass.border}`,
    backdropFilter: glass.backdropBlur,

    // Custom scrollbar
    '&::-webkit-scrollbar': {
      height: '6px',
    },
    '&::-webkit-scrollbar-track': {
      background: $isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
      borderRadius: '3px',
    },
    '&::-webkit-scrollbar-thumb': {
      background: $isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
      borderRadius: '3px',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: $isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
    },
  };
});

interface StyledStageCardProps {
  $isDark: boolean;
  $neonAccent: NeonAccent;
  $isSelected: boolean;
  $isCompact: boolean;
  $isClickable: boolean;
}

const StyledStageCard = styled(motion.div, {
  shouldForwardProp: (prop) => !String(prop).startsWith('$'),
})<StyledStageCardProps>(({ $isDark, $neonAccent, $isSelected, $isCompact, $isClickable }) => {
  const neonStyles = getNeonStyles($neonAccent, $isDark);
  const glow = neonGlow[$neonAccent];

  return {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: $isCompact ? '12px 16px' : '16px 24px',
    minWidth: $isCompact ? 100 : 140,
    borderRadius: 12,
    background: $isSelected ? neonStyles.background : 'transparent',
    border: `2px solid ${$isSelected ? neonStyles.border : 'transparent'}`,
    boxShadow: $isSelected ? glow.subtle : 'none',
    cursor: $isClickable ? 'pointer' : 'default',
    transition: transitionPresets.card,
    position: 'relative',

    '&:hover': $isClickable
      ? {
          background: neonStyles.background,
          border: `2px solid ${neonStyles.border}`,
          boxShadow: glow.subtle,
        }
      : {},
  };
});

const StageColorIndicator = styled(
  Box,
  transientOptions,
)<{
  $color: string;
  $isCompact: boolean;
}>(({ $color, $isCompact }) => ({
  width: $isCompact ? 8 : 10,
  height: $isCompact ? 8 : 10,
  borderRadius: '50%',
  backgroundColor: $color,
  boxShadow: `0 0 8px ${$color}`,
  marginBottom: 8,
}));

const StageCount = styled(
  Typography,
  transientOptions,
)<{ $neonAccent: NeonAccent }>(({ $neonAccent }) => ({
  fontSize: '1.5rem',
  fontWeight: 700,
  lineHeight: 1,
  color: neonAccents[$neonAccent].base,
  marginTop: 4,
}));

const SLABadge = styled(
  Box,
  transientOptions,
)<{
  $status: SLAStatus;
}>(({ $status }) => {
  const statusColors: Record<SLAStatus, NeonAccent> = {
    on_track: 'green',
    warning: 'amber',
    breached: 'red',
  };

  const accent = statusColors[$status];
  const colors = neonAccents[accent];

  return {
    position: 'absolute',
    top: 4,
    right: 4,
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: colors.base,
    boxShadow: `0 0 6px ${colors.glow}`,
    animation: $status === 'breached' ? 'pulse-sla 1.5s ease-in-out infinite' : 'none',

    '@keyframes pulse-sla': {
      '0%, 100%': {
        opacity: 1,
        transform: 'scale(1)',
      },
      '50%': {
        opacity: 0.6,
        transform: 'scale(1.3)',
      },
    },
  };
});

const StageTooltip = styled(motion.div, {
  shouldForwardProp: (prop) => !String(prop).startsWith('$'),
})<{ $isDark: boolean }>(({ $isDark }) => {
  const glass = $isDark ? glassEffects.dark : glassEffects.light;

  return {
    position: 'absolute',
    bottom: '100%',
    left: '50%',
    transform: 'translateX(-50%)',
    marginBottom: 8,
    padding: '10px 14px',
    borderRadius: 10,
    background: glass.backgroundHover,
    border: `1px solid ${glass.borderHover}`,
    backdropFilter: glass.backdropBlur,
    boxShadow: glass.shadow,
    zIndex: 100,
    minWidth: 160,
    whiteSpace: 'nowrap',

    '&::after': {
      content: '""',
      position: 'absolute',
      top: '100%',
      left: '50%',
      marginLeft: -6,
      borderWidth: 6,
      borderStyle: 'solid',
      borderColor: `${glass.backgroundHover} transparent transparent transparent`,
    },
  };
});

const FinalStageBadge = styled(
  Box,
  transientOptions,
)<{
  $isSuccess: boolean;
  $isFailure: boolean;
  $isDark: boolean;
}>(({ $isSuccess, $isFailure, $isDark }) => {
  const accent: NeonAccent = $isSuccess ? 'green' : $isFailure ? 'red' : 'purple';
  const neonStyles = getNeonStyles(accent, $isDark);

  return {
    position: 'absolute',
    bottom: -4,
    right: -4,
    width: 16,
    height: 16,
    borderRadius: '50%',
    background: neonStyles.background,
    border: `1px solid ${neonStyles.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    '& svg': {
      width: 10,
      height: 10,
      stroke: neonAccents[accent].base,
      strokeWidth: 2.5,
    },
  };
});

// ============================================================================
// Helper Functions
// ============================================================================

const getStageColor = (stage: PipelineStage): string => {
  return neonAccents[stage.color]?.base || neonAccents.cyan.base;
};

// ============================================================================
// Component
// ============================================================================

export function PipelineView({
  pipeline,
  stageCounts = {},
  selectedStageId,
  onStageClick,
  showSLA = false,
  slaStatus = {},
  neonAccent = 'cyan',
  variant = 'compact',
  className,
}: PipelineViewProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isCompact = variant === 'compact';

  // Hover state for tooltip
  const [hoveredStageId, setHoveredStageId] = useState<string | null>(null);

  // Sort stages by order
  const sortedStages = useMemo(() => {
    return [...pipeline.stages].sort((a, b) => a.order - b.order);
  }, [pipeline.stages]);

  // Handle stage click
  const handleStageClick = useCallback(
    (stage: PipelineStage) => {
      if (onStageClick) {
        onStageClick(stage);
      }
    },
    [onStageClick],
  );

  return (
    <StyledContainer $isDark={isDark} className={className}>
      <AnimatePresence>
        {sortedStages.map((stage, index) => (
          <Box
            key={stage.id}
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <StyledStageCard
              $isDark={isDark}
              $neonAccent={stage.color || neonAccent}
              $isSelected={selectedStageId === stage.id}
              $isCompact={isCompact}
              $isClickable={!!onStageClick}
              onClick={() => handleStageClick(stage)}
              onMouseEnter={() => setHoveredStageId(stage.id)}
              onMouseLeave={() => setHoveredStageId(null)}
              initial={motionPresets.fadeInUp.initial}
              animate={motionPresets.fadeInUp.animate}
              transition={{
                ...motionPresets.fadeInUp.transition,
                delay: index * 0.05,
              }}
              whileHover={onStageClick ? { scale: 1.02, y: -2 } : undefined}
              whileTap={onStageClick ? { scale: 0.98 } : undefined}
            >
              {/* SLA indicator */}
              {showSLA && slaStatus[stage.id] && <SLABadge $status={slaStatus[stage.id]} />}

              {/* Stage color indicator */}
              <StageColorIndicator $color={getStageColor(stage)} $isCompact={isCompact} />

              {/* Stage name */}
              <Typography
                variant={isCompact ? 'caption' : 'body2'}
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  textAlign: 'center',
                  lineHeight: 1.2,
                }}
              >
                {stage.name}
              </Typography>

              {/* Stage count */}
              <StageCount $neonAccent={stage.color || neonAccent} variant="h5">
                {stageCounts[stage.id] ?? 0}
              </StageCount>

              {/* Final stage badge */}
              {stage.isFinal && (
                <FinalStageBadge $isSuccess={!!stage.isSuccess} $isFailure={!!stage.isFailure} $isDark={isDark}>
                  {stage.isSuccess ? (
                    <svg viewBox="0 0 24 24" fill="none">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  ) : stage.isFailure ? (
                    <svg viewBox="0 0 24 24" fill="none">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="4" />
                    </svg>
                  )}
                </FinalStageBadge>
              )}

              {/* Hover tooltip (detailed view) */}
              {variant === 'detailed' && hoveredStageId === stage.id && (
                <StageTooltip
                  $isDark={isDark}
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 5 }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      display: 'block',
                      mb: 0.5,
                    }}
                  >
                    {stage.name}
                  </Typography>
                  {stage.description && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        display: 'block',
                        mb: 1,
                      }}
                    >
                      {stage.description}
                    </Typography>
                  )}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 2,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      Records:
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                      }}
                    >
                      {stageCounts[stage.id] ?? 0}
                    </Typography>
                  </Box>
                  {stage.sla && (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 2,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                        }}
                      >
                        SLA:
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                        }}
                      >
                        {stage.sla.targetHours}h
                      </Typography>
                    </Box>
                  )}
                  {stage.wipLimit && (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 2,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                        }}
                      >
                        WIP Limit:
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                        }}
                      >
                        {stage.wipLimit}
                      </Typography>
                    </Box>
                  )}
                </StageTooltip>
              )}
            </StyledStageCard>

            {/* Arrow between stages */}
            {index < sortedStages.length - 1 && (
              <Box sx={{ mx: 1 }}>
                <TransitionArrow
                  neonAccent={stage.color || neonAccent}
                  animated={true}
                  size={isCompact ? 'small' : 'medium'}
                />
              </Box>
            )}
          </Box>
        ))}
      </AnimatePresence>
    </StyledContainer>
  );
}

PipelineView.displayName = 'PipelineView';

export default PipelineView;
